/**
 * Kroger banner registry -- the single source of truth for the Kroger family
 * of brands.
 *
 * WHY: Kroger's /v1/locations returns a `chain` code (e.g. "KINGSOOPERS")
 * but NO storefront hostname, while its Cart API is one account-wide cart
 * shared across every banner. Before this table the handoff returned a
 * hardcoded https://www.kroger.com/cart to everyone, so a King Soopers
 * shopper landed on the wrong storefront (bug report 2026-08-17).
 *
 * The chain codes below were READ FROM Kroger's certification API on
 * 2026-08-17 by probing /v1/locations across banner territories -- not
 * guessed. Guessing would have produced "HARRISTEETER" (real value: "HART")
 * and "FREDMEYER" (real value: "FRED"), and would have missed that
 * "METRO MARKET" and "PICK N SAVE" contain spaces. All 18 hosts resolved
 * via DNS the same day; 17 of the 18 share one Akamai IP, which is why one
 * cart spans them.
 *
 * SHARED, not API-local: the web app needs `displayName` and the API needs
 * `host`. One table means the two cannot drift -- the same discipline that
 * put the grocery purchase predicate in ./grocery-bucket.ts.
 */

export interface KrogerBanner {
  /** Kroger's `chain` value, verbatim as the API returns it. */
  chain: string;
  /** Consumer-facing brand name, as the banner writes it. */
  displayName: string;
  /** Storefront host, no scheme, no trailing slash. */
  host: string;
  /**
   * Whether this banner has usable Kroger-platform ecommerce.
   *
   * Only RULER is false: Ruler Foods locations return HTTP 200 with ZERO
   * products from /v1/products, so every grocery item would come back
   * unmatched there. This is an explicit per-banner flag and NOT a general
   * "no products means no store" rule -- the observation comes from the
   * certification environment, whose catalog is thin.
   */
  ecommerce: boolean;
}

export const KROGER_DEFAULT_HOST = "www.kroger.com";
export const KROGER_DEFAULT_NAME = "Kroger";

/**
 * The persisted "we asked, and this store has no banner" marker for
 * RetailerConnection.storeChain.
 *
 * The column has three states, not two:
 *   null                    -- never resolved (a row predating the column)
 *   KROGER_BANNER_NONE      -- resolved, and Kroger gave us no chain
 *   "KINGSOOPERS", ...      -- resolved to a real banner
 *
 * Without the middle state the API's lazy backfill can never record a
 * negative answer, so `storeLocationId && !storeChain` stays true forever and
 * every later cart push pays another app-token + locations round trip.
 *
 * INVARIANT: this value must NOT resolve through lookupKrogerBanner, so a
 * connection carrying it still lands on the kroger.com fallback rather than
 * some arbitrary banner's storefront. Pinned by the "KROGER_BANNER_NONE"
 * test in kroger-banners.test.ts.
 */
export const KROGER_BANNER_NONE = "";

function banner(
  chain: string,
  displayName: string,
  host: string,
  ecommerce = true,
): KrogerBanner {
  return { chain, displayName, host, ecommerce };
}

/**
 * Null-prototype: the keys are upstream-supplied strings, so on a plain
 * object literal `KROGER_BANNERS["constructor"]` would return Object's
 * constructor and every downstream `.host` read would go undefined. Same
 * defect, same fix as ALLERGY_CLASS_SYNONYMS in recipe-eligibility.ts
 * (#407).
 */
export const KROGER_BANNERS: Record<string, KrogerBanner> = Object.assign(
  Object.create(null) as Record<string, KrogerBanner>,
  {
    KROGER: banner("KROGER", "Kroger", "www.kroger.com"),
    KINGSOOPERS: banner("KINGSOOPERS", "King Soopers", "www.kingsoopers.com"),
    FRED: banner("FRED", "Fred Meyer", "www.fredmeyer.com"),
    QFC: banner("QFC", "QFC", "www.qfc.com"),
    RALPHS: banner("RALPHS", "Ralphs", "www.ralphs.com"),
    FRYS: banner("FRYS", "Fry's Food and Drug", "www.frysfood.com"),
    SMITHS: banner("SMITHS", "Smith's", "www.smithsfoodanddrug.com"),
    DILLONS: banner("DILLONS", "Dillons", "www.dillons.com"),
    BAKERS: banner("BAKERS", "Baker's", "www.bakersplus.com"),
    GERBES: banner("GERBES", "Gerbes", "www.gerbes.com"),
    CITYMARKET: banner("CITYMARKET", "City Market", "www.citymarket.com"),
    HART: banner("HART", "Harris Teeter", "www.harristeeter.com"),
    MARIANOS: banner("MARIANOS", "Mariano's", "www.marianos.com"),
    "METRO MARKET": banner("METRO MARKET", "Metro Market", "www.metromarket.net"),
    "PICK N SAVE": banner("PICK N SAVE", "Pick 'n Save", "www.picknsave.com"),
    FOOD4LESS: banner("FOOD4LESS", "Food 4 Less", "www.food4less.com"),
    FOODSCO: banner("FOODSCO", "Foods Co", "www.foodsco.net"),
    RULER: banner("RULER", "Ruler Foods", "www.rulerfoods.com", false),
  },
);

/**
 * Uppercase and trim only. Do NOT strip or collapse internal whitespace --
 * "METRO MARKET" and "PICK N SAVE" are real chain codes.
 */
function normalizeChain(chain: string | null | undefined): string {
  return typeof chain === "string" ? chain.trim().toUpperCase() : "";
}

export function lookupKrogerBanner(
  chain: string | null | undefined,
): KrogerBanner | null {
  const key = normalizeChain(chain);
  if (!key) return null;
  return KROGER_BANNERS[key] ?? null;
}

/** The banner's own cart page; kroger.com when the banner is unknown. */
export function krogerCartUrl(chain: string | null | undefined): string {
  const host = lookupKrogerBanner(chain)?.host ?? KROGER_DEFAULT_HOST;
  return `https://${host}/cart`;
}

/** The banner's brand name; "Kroger" when the banner is unknown. */
export function krogerBannerName(chain: string | null | undefined): string {
  return lookupKrogerBanner(chain)?.displayName ?? KROGER_DEFAULT_NAME;
}

/**
 * True ONLY for banners we have verified cannot complete our flow.
 * An unknown chain is deliberately NOT cartless: hiding a real store a
 * shopper needs is worse than an imperfect cart link.
 */
export function isCartlessKrogerBanner(
  chain: string | null | undefined,
): boolean {
  const found = lookupKrogerBanner(chain);
  return found !== null && !found.ecommerce;
}
